import {
  Heart,
  Library,
  Lightbulb,
  Mail,
  ListMusic,
  LogOut,
  Mic,
  Menu,
  MessageCircle,
  Monitor,
  Plus,
  RadioTower,
  Settings,
  Store,
  Tag,
  Upload,
  UserRound,
  Video,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useIsSeller } from '@/hooks/useIsSeller';
import { useOwnerFocus } from '@/hooks/useOwnerFocus';
import { buildAuthHref } from '@/routing/authReturnTo';
import { mainSiteUrl, studioUrl } from '@/routing/studioHost';
import { Logo } from '@/components/ui/Logo';
import SkipLink from '@/components/layout/SkipLink';
import { useAccountTheme } from '@/components/layout/useAccountTheme';

import './accountLayout.css';

type AccountNavItem = {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Lives on the studio origin, so it renders as a full-page link. */
  studio?: boolean;
};

const ACCOUNT_NAVIGATION: ReadonlyArray<AccountNavItem> = [
  { label: 'Library', to: '/my-downloads', icon: Library },
  { label: 'Saved', to: '/favorites', icon: Heart },
  { label: 'Your sequences', to: '/my-sequences', icon: ListMusic },
  { label: 'My displays', to: '/my-displays', icon: Monitor },
  { label: 'Show tools', to: '/for-owners/show', icon: RadioTower },
  // Lighting studio is hidden until launch; the owner reaches it from the admin sidebar (Ops).
  { label: 'Sales & payouts', to: '/seller-dashboard', icon: Store },
  { label: 'Promotions', to: '/seller/promotions', icon: Tag },
  { label: 'Profile', to: '/profile', icon: UserRound },
  { label: 'Messages', to: '/messages', icon: MessageCircle },
];

// studio.lightshunt.com mounts the Studio pages at the root of its own origin.
const STUDIO_NAVIGATION: ReadonlyArray<AccountNavItem> = [
  { label: 'Projects', to: '/projects', icon: Lightbulb },
  { label: 'Studio settings', to: '/settings', icon: Settings },
];

const ACCOUNT_CREATION_ACTIONS = [
  { label: 'Add a display', to: '/display/edit', icon: Plus },
  { label: 'Upload a sequence', to: '/upload-sequence', icon: Upload },
  { label: 'Add a video', to: '/videos/upload', icon: Video },
  { label: 'Earn free voiceovers', to: '/voices', icon: Mic },
] satisfies ReadonlyArray<{ label: string; to: string; icon: LucideIcon }>;

// Hidden for owners who only run a show, unless they have listed, connected Stripe or sold
// (useIsSeller). The routes stay reachable by URL.
const SELL_NAV_PATHS: ReadonlyArray<string> = ['/seller-dashboard', '/seller/promotions'];
const UPLOAD_ACTION_PATH = '/upload-sequence';

const BROWSE_NAVIGATION = [
  { label: 'Browse', to: '/' },
  { label: 'Displays', to: '/displays/map' },
  { label: 'Sequences', to: '/sequences' },
] as const;

const AccountPrivacyMetadata = () => (
  <Helmet>
    <meta name="robots" content="noindex,nofollow" />
  </Helmet>
);

const isAccountPathActive = (pathname: string, target: string) => (
  pathname === target || pathname.startsWith(`${target}/`)
);

const getAccountDisplayName = (user: { name?: string; email?: string }) => (
  user.name?.trim() || user.email?.split('@')[0] || 'Account'
);

const getAccountInitials = (displayName: string) => displayName
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0]?.toUpperCase())
  .join('') || 'A';

type AccountLayoutProps = {
  /**
   * Lets a viewport-owned child (Messages, the Studio project editor) fill the
   * area beside the rail without a nested <main> landmark or a second viewport.
   * It changes layout only; every account page shares one theme.
   */
  fullBleed?: boolean;
  /** 'studio' is the studio.lightshunt.com shell: Studio nav, marketplace links leave the origin. */
  product?: 'marketplace' | 'studio';
};

const AccountLayout = ({ fullBleed = false, product = 'marketplace' }: AccountLayoutProps) => {
  const isStudio = product === 'studio';
  const showOnly = useOwnerFocus().focus === 'show';
  const isSeller = useIsSeller(showOnly);
  const hideSelling = showOnly && isSeller === false;
  const navigation = isStudio
    ? STUDIO_NAVIGATION
    : ACCOUNT_NAVIGATION.filter(({ to }) => !(hideSelling && SELL_NAV_PATHS.includes(to)));
  const creationActions = ACCOUNT_CREATION_ACTIONS.filter(({ to }) => !(showOnly && to === UPLOAD_ACTION_PATH));
  const { user, isLoading, logout } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const activeLinkRef = useRef<HTMLAnchorElement | null>(null);
  const mainContentRef = useRef<HTMLDivElement | null>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const focusMainAfterNavigationRef = useRef(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  // Longest match wins, so a nested path lights up only its closest item.
  const activeNavigation = navigation
    .filter(({ to, studio }) => !studio && isAccountPathActive(location.pathname, to))
    .sort((a, b) => b.to.length - a.to.length)[0];
  const accountDisplayName = user ? getAccountDisplayName(user) : 'Account';
  const accountInitials = getAccountInitials(accountDisplayName);
  const accountAvatarUrl = user?.user_metadata?.avatar_url || user?.picture;

  useAccountTheme();

  useEffect(() => {
    const activeLink = activeLinkRef.current;
    if (!activeLink || typeof activeLink.scrollIntoView !== 'function') return;
    activeLink.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeNavigation?.to]);

  useEffect(() => {
    if (!focusMainAfterNavigationRef.current) return;
    focusMainAfterNavigationRef.current = false;
    mainContentRef.current?.focus({ preventScroll: true });
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!mobileNavOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setMobileNavOpen(false);
      mobileMenuButtonRef.current?.focus();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileNavOpen]);

  if (isLoading) {
    return (
      <div className="account-shell account-shell--pending">
        <AccountPrivacyMetadata />
        <div className="account-session-state" role="status" aria-live="polite">
          Checking your session…
        </div>
      </div>
    );
  }

  if (!user) {
    // A deliberate sign-out goes home, not back to sign-in with this page as returnTo.
    if (signingOut) return <Navigate replace to="/" />;
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return (
      <div className="account-shell account-shell--pending">
        <AccountPrivacyMetadata />
        <Navigate replace to={buildAuthHref('/auth', returnTo)} />
      </div>
    );
  }

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await logout();
      toast({ title: 'Signed out', description: 'You have been signed out successfully.' });
    } catch {
      setSigningOut(false);
      toast({
        title: 'Sign out failed',
        description: 'Check your connection and try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className={fullBleed ? 'account-shell account-shell--full-height' : 'account-shell'}>
      <SkipLink />
      <AccountPrivacyMetadata />
      <header className="account-header">
        <button
          type="button"
          ref={mobileMenuButtonRef}
          className="account-header__menu"
          aria-controls="account-sidebar"
          aria-expanded={mobileNavOpen}
          aria-label={mobileNavOpen ? 'Close account navigation' : 'Open account navigation'}
          onClick={() => setMobileNavOpen((open) => !open)}
        >
          {mobileNavOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
        {isStudio ? (
          <NavLink to="/projects" className="account-header__brand" aria-label="Light Hunt Studio projects">
            <Logo />
          </NavLink>
        ) : (
          <NavLink to="/" className="account-header__brand" aria-label="Return to Light Hunt">
            <Logo />
          </NavLink>
        )}
        <nav className="account-header__browse" aria-label="Browse Light Hunt">
          {BROWSE_NAVIGATION.map(({ label, to }) => (
            isStudio
              ? <a key={to} href={mainSiteUrl(to)}>{label}</a>
              : <NavLink key={to} to={to}>{label}</NavLink>
          ))}
        </nav>
        <span
          className="account-header__avatar"
          role="img"
          aria-label={`Signed in as ${accountDisplayName}`}
        >
          {accountAvatarUrl ? <img src={accountAvatarUrl} alt="" /> : accountInitials}
        </span>
        {isStudio ? (
          <a href={mainSiteUrl('/')} className="account-header__exit">Back to Light Hunt</a>
        ) : (
          <NavLink to="/" className="account-header__exit">Back to Light Hunt</NavLink>
        )}
      </header>

      <p className="account-current-section">
        <span>{isStudio ? 'Light Hunt Studio' : 'Your account'}</span>{' '}
        <span aria-hidden="true">/</span>{' '}
        <strong>{activeNavigation?.label ?? 'Overview'}</strong>
      </p>

      <div className="account-shell__frame">
        <aside
          id="account-sidebar"
          className="account-sidebar"
          aria-labelledby="account-sidebar-title"
          data-mobile-open={mobileNavOpen ? 'true' : 'false'}
        >
          <h2 id="account-sidebar-title" className="account-sidebar__title">
            {isStudio ? 'Light Hunt Studio' : 'Your account'}
          </h2>
          <nav aria-label="Account navigation">
            {navigation.map(({ label, to, icon: Icon, studio }) => {
              if (studio) {
                return (
                  <a key={to} href={studioUrl(to)} className="account-nav-link">
                    <Icon aria-hidden="true" />
                    <span>{label}</span>
                  </a>
                );
              }
              const active = to === activeNavigation?.to;
              return (
                <NavLink
                  ref={active ? activeLinkRef : undefined}
                  key={to}
                  to={to}
                  end={to === '/profile'}
                  aria-current={active ? 'page' : false}
                  onClick={() => {
                    focusMainAfterNavigationRef.current = to !== location.pathname;
                    setMobileNavOpen(false);
                  }}
                  className={active ? 'account-nav-link account-nav-link--active' : 'account-nav-link'}
                >
                  <Icon aria-hidden="true" />
                  <span>{label}</span>
                </NavLink>
              );
            })}
          </nav>
          {!isStudio && (
            <section className="account-sidebar__actions" aria-labelledby="account-actions-title">
              <h3 id="account-actions-title">Share your work</h3>
              <div className="account-sidebar__action-list">
                {creationActions.map(({ label, to, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className="account-action-link"
                    onClick={() => {
                      focusMainAfterNavigationRef.current = to !== location.pathname;
                      setMobileNavOpen(false);
                    }}
                  >
                    <Icon aria-hidden="true" />
                    <span>{label}</span>
                  </NavLink>
                ))}
              </div>
            </section>
          )}
          <div className="account-sidebar__session">
            {!isStudio && (
              <NavLink to="/email-preferences" className="account-nav-link account-preferences-link">
                <Mail aria-hidden="true" />
                <span>Email preferences</span>
              </NavLink>
            )}
            <button
              type="button"
              className="account-nav-link account-signout"
              onClick={handleSignOut}
              disabled={signingOut}
            >
              <LogOut aria-hidden="true" />
              <span>{signingOut ? 'Signing out…' : 'Sign out'}</span>
            </button>
          </div>
        </aside>

        {fullBleed ? (
          <div
            id="main-content"
            className="account-main account-main--full-bleed"
            role="region"
            aria-label="Account content"
            tabIndex={-1}
            ref={mainContentRef}
          >
            <Outlet />
          </div>
        ) : (
          <main
            id="main-content"
            ref={mainContentRef}
            className="account-main"
            tabIndex={-1}
            aria-label="Account workspace"
          >
            <Outlet />
          </main>
        )}
      </div>
    </div>
  );
};

export default AccountLayout;
